import React, { useState, useMemo } from "react";
import {
  Box,
  Stack,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Typography,
  Divider
} from "@mui/material";

const JumboNowiseReport = () => {

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // ✅ Dummy Data (As per Image Structure)
  const dummyData = [
    {
      date: "2026-01-26",
      jumbo_no: "26A0001",
      quality: "A",
      gsm: 80,

      jumbo_production: 10,
      jumbo_wip: 3,

      rw_g1: 4.5,
      rw_sl: 1.0,
      rw_buffer: 1.0,
      rw_repulp: 0.5,
      rw_wip: 0,

      reel_g1: 0,
      reel_sl: 0,
      reel_buffer: 0,
      reel_wip: 0,

      sheet_g1: 0,
      sheet_sl: 0,
      sheet_repulp: 0,
      sheet_wip: 0,

      ream_g1: 0,
      ream_sl: 0,
      ream_repulp: 0,
      ream_wip: 0,

      bundle_g1: 0,
      bundle_sl: 0,
      bundle_repulp: 0,
      bundle_wip: 0,

      fin_g1: 0,
      fin_sl: 0,
      fin_repulp: 0,
      fin_wip: 0,

      repulp_jumbo_percent: "5%",
      repulp_rw_percent: "2%"
    },

    // More sample jumbo numbers like image
    ...Array.from({ length: 9 }).map((_, i) => ({
      date: "2026-01-26",
      jumbo_no: `26A000${i + 2}`,
      quality: "",
      gsm: "",

      jumbo_production: "",
      jumbo_wip: "",

      rw_g1: "",
      rw_sl: "",
      rw_buffer: "",
      rw_repulp: "",
      rw_wip: "",

      reel_g1: "",
      reel_sl: "",
      reel_buffer: "",
      reel_wip: "",

      sheet_g1: "",
      sheet_sl: "",
      sheet_repulp: "",
      sheet_wip: "",

      ream_g1: "",
      ream_sl: "",
      ream_repulp: "",
      ream_wip: "",

      bundle_g1: "",
      bundle_sl: "",
      bundle_repulp: "",
      bundle_wip: "",

      fin_g1: "",
      fin_sl: "",
      fin_repulp: "",
      fin_wip: "",

      repulp_jumbo_percent: "",
      repulp_rw_percent: ""
    }))
  ];

  // ✅ Date Filter
  const filteredData = useMemo(() => {
    if (!fromDate || !toDate) return dummyData;

    return dummyData.filter(row => {
      const rowDate = new Date(row.date);
      return rowDate >= new Date(fromDate) &&
             rowDate <= new Date(toDate);
    });
  }, [fromDate, toDate]);

  return (
    <Box p={2}>
      <Typography variant="h5" fontWeight={600} mb={2}>
        Jumbo No Wise Tracking Report
      </Typography>

      {/* Date Filters */}
      <Stack direction="row" spacing={2} mb={2}>
        <TextField
          type="date"
          label="From Date"
          size="small"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          type="date"
          label="To Date"
          size="small"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <Button
          variant="contained"
          color="secondary"
        >
          Apply
        </Button>
      </Stack>

      <Divider sx={{ mb: 2 }} />

      <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
        <Table size="small" stickyHeader>

          {/* GROUP HEADER */}
          <TableHead>
            <TableRow>
              <TableCell rowSpan={2}><b>Date</b></TableCell>
              <TableCell rowSpan={2}><b>Jumbo No</b></TableCell>
              <TableCell rowSpan={2}><b>Quality</b></TableCell>
              <TableCell rowSpan={2}><b>GSM</b></TableCell>

              <TableCell colSpan={2} align="center" sx={{ bgcolor: "#A5D6A7" }}>
                <b>Jumbo</b>
              </TableCell>

              <TableCell colSpan={5} align="center" sx={{ bgcolor: "#FFF176" }}>
                <b>Rewinder</b>
              </TableCell>

              <TableCell colSpan={4} align="center" sx={{ bgcolor: "#C8E6C9" }}>
                <b>Reel</b>
              </TableCell>

              <TableCell colSpan={4} align="center" sx={{ bgcolor: "#FFE082" }}>
                <b>Sheet</b>
              </TableCell>

              <TableCell colSpan={4} align="center" sx={{ bgcolor: "#AED581" }}>
                <b>Ream</b>
              </TableCell>

              <TableCell colSpan={4} align="center" sx={{ bgcolor: "#FFCC80" }}>
                <b>Bundle</b>
              </TableCell>

              <TableCell colSpan={4} align="center" sx={{ bgcolor: "#B39DDB" }}>
                <b>Finishing</b>
              </TableCell>

              <TableCell colSpan={2} align="center" sx={{ bgcolor: "#EF9A9A" }}>
                <b>Repulp %</b>
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell>Production</TableCell>
              <TableCell>WIP</TableCell>

              <TableCell>G1</TableCell>
              <TableCell>SL</TableCell>
              <TableCell>Buffer</TableCell>
              <TableCell>Repulp</TableCell>
              <TableCell>WIP</TableCell>

              <TableCell>G1</TableCell>
              <TableCell>SL</TableCell>
              <TableCell>Buffer</TableCell>
              <TableCell>WIP</TableCell>

              <TableCell>G1</TableCell>
              <TableCell>SL</TableCell>
              <TableCell>Repulp</TableCell>
              <TableCell>WIP</TableCell>

              <TableCell>G1</TableCell>
              <TableCell>SL</TableCell>
              <TableCell>Repulp</TableCell>
              <TableCell>WIP</TableCell>

              <TableCell>G1</TableCell>
              <TableCell>SL</TableCell>
              <TableCell>Repulp</TableCell>
              <TableCell>WIP</TableCell>

              <TableCell>G1</TableCell>
              <TableCell>SL</TableCell>
              <TableCell>Repulp</TableCell>
              <TableCell>WIP</TableCell>

              <TableCell>Jumbo %</TableCell>
              <TableCell>Rewinder %</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredData.map((row, index) => (
              <TableRow key={index}>
                {Object.values(row).map((val, i) => (
                  <TableCell key={i}>{val}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>

        </Table>
      </TableContainer>
    </Box>
  );
};

export default JumboNowiseReport;